// Visual building blocks shared by every role: icons, the number plate,
// fuel colours and the indent ticket.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FUEL_TYPE_LABELS, type FuelType } from '@shared/domain';
import { formatINR } from '@shared/money';
import type { IndentView } from './types';

/** Decorative when paired with the visible brand name or a labelled home link. */
export function BrandLogo({ size = 40 }: { size?: number }) {
  return <img className="brand-logo" src="/brand/superindent-logo-v1.png" alt="" width={size} height={size} />;
}

const PATHS = {
  home: 'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  ticket: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4zM14 5v14',
  approve: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 12.5l3 3 5-6',
  bills: 'M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6M9 16h3',
  more: 'M4 7h16M4 12h16M4 17h16',
  bell: 'M6 16V11a6 6 0 1 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0',
  sun: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M5 19l1.5-1.5M17.5 6.5L19 5',
  moon: 'M20.9 13a9 9 0 0 1-9.9-9.9A9 9 0 1 0 20.9 13z',
  fuel: 'M3 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16M2 21h13M5 8h7M14 9h2a2 2 0 0 1 2 2v5a1.5 1.5 0 0 0 3 0V8l-3-3',
  plus: 'M12 5v14M5 12h14',
  back: 'M15 6l-6 6 6 6',
  next: 'M9 6l6 6-6 6',
  speaker: 'M4 9h4l5-4v14l-5-4H4zM16.5 8.5a5 5 0 0 1 0 7',
  qr: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h3v3h-3zM20 14v7M14 20h3',
  people: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21a7 7 0 0 1 14 0M16 3.5a4 4 0 0 1 0 7.5M18 14a6 6 0 0 1 4 7',
  store: 'M4 10v10h16V10M3 4h18l-1 6H4zM9 20v-5h6v5',
  wallet: 'M3 7a2 2 0 0 1 2-2h13v4M3 7v11a2 2 0 0 0 2 2h15V9H5a2 2 0 0 1-2-2zM16 14h2',
  rules: 'M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M16 4v4M10 10v4M18 16v4',
  chart: 'M4 20V10M10 20V4M16 20v-8M22 20H2',
  history: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14M12 17.5v.01',
  wrench: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z',
  book: 'M4 4h11a3 3 0 0 1 3 3v14H7a3 3 0 0 1-3-3zM4 18a3 3 0 0 1 3-3h11',
  alert: 'M12 3l10 18H2zM12 10v5M12 18v.01',
  edit: 'M4 20h4L19 9l-4-4L4 16zM13 7l4 4',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  logout: 'M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 16l-4-4 4-4M6 12h10',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  cross: 'M6 6l12 12M18 6L6 18',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  truck: 'M2 6h11v10H2zM13 9h4l4 4v3h-8M6 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  minus: 'M5 12h14',
  camera: 'M4 8h3l2-3h6l2 3h3v11H4zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  phone: 'M7 3h10a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM11 18h2',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v5M12 8v.01',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 22, stroke = 2, className }: { name: IconName; size?: number; stroke?: number; className?: string }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d={PATHS[name]} />
    </svg>
  );
}

/** "₹2,000" for whole rupees, "₹2,000.50" otherwise. */
export function rs(paise?: string | null): string {
  if (paise === undefined || paise === null) return '—';
  return formatINR(paise, { paise: BigInt(paise) % 100n !== 0n });
}

/** "11:59 pm" in India time. */
export function clockTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).toLowerCase();
}

/** True when the timestamp falls on today's date in India. */
export function isTodayIst(iso?: string | null): boolean {
  if (!iso) return false;
  const d = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  return d(new Date(iso)) === d(new Date());
}

export function fuelTone(fuel?: string | null): 'petrol' | 'diesel' | 'cng' | 'other' {
  if (fuel === 'PETROL' || fuel === 'PREMIUM_PETROL') return 'petrol';
  if (fuel === 'DIESEL' || fuel === 'PREMIUM_DIESEL') return 'diesel';
  if (fuel === 'CNG') return 'cng';
  return 'other';
}

export function fuelName(fuel?: string | null, fallback = 'Fuel'): string {
  return fuel && fuel in FUEL_TYPE_LABELS ? FUEL_TYPE_LABELS[fuel as FuelType] : fallback;
}

/** "MH12AB1234" → "MH 12 AB 1234"; anything unusual is shown as typed. */
export function plateText(reg?: string | null): string {
  if (!reg) return '';
  const raw = reg.toUpperCase().replace(/[\s-]/g, '');
  const m = /^([A-Z]{2})(\d{1,2})([A-Z]{0,3})(\d{1,4})$/.exec(raw);
  return m ? [m[1], m[2], m[3], m[4]].filter(Boolean).join(' ') : reg.toUpperCase();
}

export function NumberPlate({ reg, size = 'md' }: { reg?: string | null; size?: 'sm' | 'md' | 'lg' }) {
  if (!reg) return null;
  return (
    <span className={`plate plate-${size}`} aria-label={`Vehicle ${plateText(reg)}`}>
      <span className="plate-ind" aria-hidden="true">IND</span>
      <span className="plate-num">{plateText(reg)}</span>
    </span>
  );
}

export type TicketState = 'ready' | 'waiting' | 'filling' | 'done' | 'closed';

const STATUS_PILL: Record<string, [string, TicketState]> = {
  DRAFT: ['Draft', 'waiting'],
  REQUESTED: ['Waiting', 'waiting'],
  APPROVED: ['Ready', 'ready'],
  CLAIMED: ['Filling now', 'filling'],
  FULFILLED: ['Filled', 'done'],
  REJECTED: ['Rejected', 'closed'],
  REVOKED: ['Stopped', 'closed'],
  EXPIRED: ['Expired', 'closed'],
  CANCELLED: ['Cancelled', 'closed'],
};

export function statusPill(status: string): [string, TicketState] {
  return STATUS_PILL[status] ?? [status.toLowerCase(), 'closed'];
}

export interface TicketData {
  fuelType?: string | null;
  plate?: string | null;
  driver?: string | null;
  limit: ReactNode;
  till?: string | null;
  status?: string;
  statusLabel?: string;
  top?: ReactNode;
  extra?: ReactNode;
}

/** The indent as a ticket: coloured fuel stub, number plate, driver and limit. */
export function Ticket({ t, to, actions, as = 'li' }: { t: TicketData; to?: string; actions?: ReactNode; as?: 'li' | 'div' }) {
  const tone = fuelTone(t.fuelType);
  const [pill, state] = t.status ? statusPill(t.status) : ['', 'ready' as TicketState];
  const body = (
    <>
      <div className={`ticket-stub fuel-${tone}`}>
        <Icon name="fuel" size={26} />
        <span>{fuelName(t.fuelType)}</span>
      </div>
      <div className="ticket-body">
        {t.top ? <div className="ticket-top">{t.top}</div> : null}
        <div className="ticket-head">
          {t.plate ? <span className="ticket-plate">{plateText(t.plate)}</span> : <span className="ticket-plate muted-plate">Any vehicle</span>}
          {pill ? <span className={`pill pill-${state}`}>{t.statusLabel ?? pill}</span> : null}
        </div>
        <div className="ticket-line">
          {t.driver ? <span className="ticket-driver">{t.driver}</span> : null}{t.driver && t.limit ? ' · ' : ''}{t.limit}{t.till ? <> · till {clockTime(t.till)}</> : null}
        </div>
        {t.extra}
      </div>
    </>
  );
  const Tag = as;
  return (
    <Tag className={`ticket ticket-${state}`}>
      {to ? <Link className="ticket-main" to={to}>{body}</Link> : <div className="ticket-main">{body}</div>}
      {actions ? <div className="ticket-actions">{actions}</div> : null}
    </Tag>
  );
}

/** "up to ₹2,000", "up to 25 L" or "Full tank · stop at ₹5,000". */
export function limitText(i: { fullTank?: boolean; limitType: 'AMOUNT' | 'QUANTITY'; maxAmountPaise?: string; maxQuantity?: string; unit?: string }): string {
  if (i.fullTank) return `Full tank · stop at ${rs(i.maxAmountPaise)}`;
  if (i.limitType === 'AMOUNT') return `up to ${rs(i.maxAmountPaise)}`;
  return `up to ${i.maxQuantity ? Number(i.maxQuantity).toString() : '—'} ${i.unit ?? 'L'}`;
}

export function greeting(): string {
  const h = Number(new Date().toLocaleString('en-GB', { hour: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }));
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/** Ticket fields for an indent as the company or driver sees it. */
export function indentTicket(i: IndentView, top?: ReactNode): TicketData {
  return {
    fuelType: i.fuelType ?? i.vehicle?.fuelType,
    plate: i.vehicle?.registrationNumber,
    driver: i.person.name || undefined,
    limit: limitText(i),
    till: ['REQUESTED', 'APPROVED', 'CLAIMED', 'DRAFT'].includes(i.status) ? i.expiresAt : undefined,
    status: i.status,
    top,
  };
}
